import type { CreditAppSearchFormData, CreditAppSearchFormErrors, CreditAppSearchFormTouched } from '../types';

/**
 * Validates last name field
 * Validates max length (50 characters) and format (letters, apostrophes, hyphens, and spaces only)
 * Note: This is more restrictive than CustomerCard which only validates max length
 */
export const validateLastName = (value: string): string | null => {
    if (!value) return null; // Not required for search

    // Validate format - only letters, apostrophes, hyphens, and spaces
    const nameRegex = /^[A-Za-z '-]*$/;
    if (!nameRegex.test(value)) {
        return "Use only letters and ' - ";
    }

    // Validate max length
    if (value.length > 50) {
        return 'Last Name must be under 50 characters';
    }

    return null;
};

/**
 * Removes formatting from phone number (hyphens, spaces, parentheses)
 */
export const stripPhoneFormatting = (value: string): string => {
    return value.replace(/[-\s()]/g, '');
};

/**
 * Validates phone number field
 * Accepts 10-digit phone numbers with or without formatting
 */
export const validatePhone = (value: string): string | null => {
    if (!value) return null; // Not required for search

    // Remove formatting for validation
    const digitsOnly = stripPhoneFormatting(value);

    // Check if it contains only digits
    if (!/^\d+$/.test(digitsOnly)) {
        return 'Phone must contain only numbers';
    }

    // Check for exactly 10 digits
    if (digitsOnly.length !== 10) {
        return 'Phone must be 10 digits';
    }

    return null;
};

/**
 * Validates email field
 * Validates max length (50 characters) and basic email format
 * Uses similar validation to CustomerCard
 */
export const validateEmail = (value: string): string | null => {
    if (!value) return null; // Not required for search

    // Validate format using simple email regex
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(value)) {
        return 'Email Address is invalid';
    }

    // Validate max length
    if (value.length > 50) {
        return 'Email Address must be under 50 characters';
    }

    return null;
};

/**
 * Validates the entire search form
 * Only validates fields that have been touched
 */
export const validateSearchForm = (
    formData: CreditAppSearchFormData,
    touched: CreditAppSearchFormTouched
): CreditAppSearchFormErrors => {
    const errors: CreditAppSearchFormErrors = {};

    if (touched.lastName) {
        const lastNameError = validateLastName(formData.lastName);
        if (lastNameError) errors.lastName = lastNameError;
    }

    if (touched.phone) {
        const phoneError = validatePhone(formData.phone);
        if (phoneError) errors.phone = phoneError;
    }

    if (touched.email) {
        const emailError = validateEmail(formData.email);
        if (emailError) errors.email = emailError;
    }

    return errors;
};

/**
 * Checks if at least one of the required search fields has a value
 * Required fields: lastName, phone, or email
 */
export const hasAtLeastOneSearchField = (formData: CreditAppSearchFormData): boolean => {
    return Boolean(formData.lastName || formData.phone || formData.email);
};

/**
 * Checks if the search button should be enabled
 * Enabled if: no validation errors AND at least one search field has a value
 */
export const isSearchButtonEnabled = (formData: CreditAppSearchFormData, errors: CreditAppSearchFormErrors): boolean => {
    const hasErrors = Object.keys(errors).length > 0;
    const hasSearchCriteria = hasAtLeastOneSearchField(formData);

    return !hasErrors && hasSearchCriteria;
};
